import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { authHeader } from '../../services/authHeader';
import useLobbySocket from '../../hooks/useLobbySocket';
import LobbyTicker from './LobbyTicker';
import styles from './Lobby.module.css';

const Lobby = () => {
    const { lobbyID } = useParams();
    const navigate = useNavigate();
    const username = useSelector((state) => state.auth.username || state.user.username);

    const [slots, setSlots] = useState([]);
    const [isCreator, setIsCreator] = useState(false);
    const [cancelledMsg, setCancelledMsg] = useState(null);
    const [playerStats, setPlayerStats] = useState({});
    const [teamRecord, setTeamRecord] = useState(null);
    const [confirmCancel, setConfirmCancel] = useState(false);
    const [wsConnected, setWsConnected] = useState(true);
    const [lobbyTerminated, setLobbyTerminated] = useState(false);
    const [countdown, setCountdown] = useState(3);
    const [error, setError] = useState(null);
    const confirmTimerRef = useRef(null);

    const fetchStats = useCallback(async (slotsData) => {
        const statPromises = slotsData.map(slot =>
            axios.get(`${process.env.REACT_APP_API_URL}/api/c/getstats/`, {
                params: { username: slot.username },
                ...authHeader(),
            })
            .then(res => ({ username: slot.username, stats: res.data.player_stats }))
            .catch(() => ({ username: slot.username, stats: null }))
        );

        const homeSlots = slotsData.filter(s => s.team?.toLowerCase() === 'home');
        const awaySlots = slotsData.filter(s => s.team?.toLowerCase() === 'away');

        let teamStatsPromise = Promise.resolve(null);
        if (homeSlots.length === 2 && awaySlots.length === 2) {
            teamStatsPromise = axios.get(`${process.env.REACT_APP_API_URL}/api/c/getteamstats/`, {
                params: {
                    username: homeSlots[0].username,
                    teammate: homeSlots[1].username,
                    opponent1: awaySlots[0].username,
                    opponent2: awaySlots[1].username,
                },
                ...authHeader(),
            })
            .then(res => res.data.stats)
            .catch(() => null);
        }

        const [statResults, teamStats] = await Promise.all([
            Promise.all(statPromises),
            teamStatsPromise,
        ]);

        const statsMap = {};
        statResults.forEach(({ username: u, stats }) => {
            if (stats) statsMap[u] = stats;
        });
        setPlayerStats(statsMap);
        if (teamStats) setTeamRecord(teamStats);
    }, []);

    const fetchLobby = useCallback(async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/g/lobby/${lobbyID}/`, authHeader());
            const data = res.data;
            setSlots(data.slots);
            setIsCreator(data.createdBy === username);
            if (data.started) {
                navigate(`/scoreboard/${data.gameID}`);
                return;
            }
            fetchStats(data.slots);
        } catch (err) {
            console.error('Error fetching lobby:', err);
        }
    }, [lobbyID, username, navigate, fetchStats]);

    useEffect(() => {
        fetchLobby();
    }, [fetchLobby]);

    // Poll lobby state when WS is disconnected so missed events are recovered
    useEffect(() => {
        if (wsConnected) return;
        const interval = setInterval(fetchLobby, 5000);
        return () => clearInterval(interval);
    }, [wsConnected, fetchLobby]);

    // Countdown timer for cancelled screen
    useEffect(() => {
        if (!cancelledMsg) return;
        setCountdown(3);
        const interval = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(interval);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [cancelledMsg]);

    // Navigate home when countdown hits 0
    useEffect(() => {
        if (cancelledMsg && countdown === 0) {
            navigate('/main');
        }
    }, [countdown, cancelledMsg, navigate]);

    // Cleanup confirm timer on unmount
    useEffect(() => {
        return () => {
            if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
        };
    }, []);

    const handleMessage = useCallback((data) => {
        if (data.type === 'player_joined') {
            setSlots((prev) =>
                prev.map((s) => s.username === data.username ? { ...s, status: 'accepted' } : s)
            );
        } else if (data.type === 'player_rejected') {
            setSlots((prev) =>
                prev.map((s) => s.username === data.username ? { ...s, status: 'rejected' } : s)
            );
        } else if (data.type === 'player_reinvited') {
            setSlots((prev) =>
                prev.map((s) => s.username === data.username ? { ...s, status: 'pending' } : s)
            );
        } else if (data.type === 'all_joined') {
            navigate(`/scoreboard/${data.gameID}`);
        } else if (data.type === 'lobby_cancelled') {
            setCancelledMsg('Game was cancelled.');
        }
    }, [navigate]);

    useLobbySocket(lobbyID, handleMessage, setWsConnected, fetchLobby, () => setLobbyTerminated(true));

    const handleCancel = async () => {
        setError(null);
        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}/api/g/lobby/${lobbyID}/`, authHeader());
        } catch (err) {
            console.error('Error cancelling lobby:', err);
            setError('Failed to cancel game. Please try again.');
        }
    };

    const handleCancelClick = () => {
        if (confirmCancel) {
            clearTimeout(confirmTimerRef.current);
            handleCancel();
        } else {
            setConfirmCancel(true);
            confirmTimerRef.current = setTimeout(() => setConfirmCancel(false), 3000);
        }
    };

    const handleReinvite = async (username) => {
        setError(null);
        try {
            await axios.post(
                `${process.env.REACT_APP_API_URL}/api/g/lobby/${lobbyID}/reinvite/`,
                { username },
                authHeader()
            );
            setSlots(prev =>
                prev.map(s => s.username === username ? { ...s, status: 'pending' } : s)
            );
        } catch (err) {
            console.error('Error re-inviting player:', err);
            setError('Failed to re-invite player. Please try again.');
        }
    };

    const handleStart = async () => {
        setError(null);
        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/api/g/lobby/${lobbyID}/start/`, {}, authHeader());
        } catch (err) {
            console.error('Error starting lobby:', err);
            setError('Failed to start game. Please try again.');
        }
    };

    if (cancelledMsg) {
        return (
            <div className={styles.container}>
                <div className={styles.cancelledCard}>
                    <div className={styles.cancelledIcon}>✕</div>
                    <h2 className={styles.cancelledHeading}>Game Cancelled</h2>
                    <p className={styles.cancelledSubtext}>Returning to home in</p>
                    <div className={styles.countdownText}>{countdown}</div>
                </div>
            </div>
        );
    }

    const homeSlots = slots.filter(s => s.team?.toLowerCase() === 'home');
    const awaySlots = slots.filter(s => s.team?.toLowerCase() === 'away');
    const acceptedCount = slots.filter(s => s.status === 'accepted').length;
    const nonCreatorSlots = slots.filter(s => !s.isCreator);
    const allReady = nonCreatorSlots.length > 0 && nonCreatorSlots.every(s => s.status === 'accepted');

    const renderSlot = (slot, i) => {
        const stats = playerStats[slot.username];
        const statusClass = slot.status === 'accepted'
            ? styles.slotAccepted
            : slot.status === 'rejected'
            ? styles.slotRejected
            : styles.slotPending;

        return (
            <div key={i} className={`${styles.slot} ${statusClass}`}>
                <div className={styles.slotMain}>
                    <div className={styles.slotNameRow}>
                        <span className={styles.slotName}>{slot.username}</span>
                        {slot.isCreator && <span className={styles.hostChip}>HOST</span>}
                    </div>
                    {stats && (
                        <span className={styles.slotStats}>
                            {stats.wins}W – {stats.losses}L
                        </span>
                    )}
                </div>
                <div className={styles.slotStatusIndicator}>
                    {slot.status === 'accepted' ? (
                        <span className={styles.statusAccepted}>✓</span>
                    ) : slot.status === 'rejected' && isCreator ? (
                        <button
                            className={styles.reinviteBtn}
                            onClick={() => handleReinvite(slot.username)}
                        >
                            Re-invite
                        </button>
                    ) : slot.status === 'rejected' ? (
                        <span className={styles.statusRejected}>✕</span>
                    ) : (
                        <span className={styles.pendingDot} />
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className={styles.container}>
            {!wsConnected && !lobbyTerminated && (
                <div className={styles.wsDisconnectedBanner}>
                    Connection lost — reconnecting…
                </div>
            )}

            {lobbyTerminated && (
                <div className={styles.terminatedOverlay}>
                    <p className={styles.terminatedMessage}>Lobby terminated due to server error.</p>
                    <button className={styles.terminatedBtn} onClick={() => navigate('/main')}>Navigate back to Home</button>
                </div>
            )}
            <h1 className={styles.title}>Lobby</h1>

            {/* Progress bar */}
            <div className={styles.progressBarWrapper}>
                <div className={styles.progressBar}>
                    {[0, 1, 2, 3].map(i => (
                        <div
                            key={i}
                            className={`${styles.progressSegment} ${i < acceptedCount ? styles.progressSegmentFilled : ''}`}
                        />
                    ))}
                </div>
                <p className={styles.progressLabel}>
                    {allReady ? 'All players ready!' : 'Waiting for players…'}
                </p>
            </div>

            {/* Lobby Intel ticker */}
            {Object.keys(playerStats).length > 0 && (
                <LobbyTicker playerStats={playerStats} slots={slots} />
            )}

            {/* Team sections */}
            {slots.length === 0 ? (
                <p className={styles.loading}>Loading lobby...</p>
            ) : (
                <div className={styles.teamsContainer}>
                    <div className={styles.teamSection}>
                        <h3 className={styles.teamLabelHome}>HOME</h3>
                        {homeSlots.map((slot, i) => renderSlot(slot, i))}
                    </div>
                    <div className={styles.teamDivider}>VS</div>
                    <div className={styles.teamSection}>
                        <h3 className={styles.teamLabelAway}>AWAY</h3>
                        {awaySlots.map((slot, i) => renderSlot(slot, i))}
                    </div>
                </div>
            )}

            {/* Head-to-head record */}
            {teamRecord && homeSlots.length === 2 && awaySlots.length === 2 && (() => {
                const viewerOnHome = homeSlots.some(s => s.username === username);
                const myTeam = viewerOnHome ? homeSlots : awaySlots;
                const opponents = viewerOnHome ? awaySlots : homeSlots;
                const teammate = myTeam.find(s => s.username !== username);
                const opp1 = opponents[0];
                const opp2 = opponents[1];
                const myWins = viewerOnHome ? teamRecord.wins : teamRecord.losses;
                const myLosses = viewerOnHome ? teamRecord.losses : teamRecord.wins;
                const isFirstMatchup = myWins === 0 && myLosses === 0;
                return (
                    <div className={styles.h2hCard}>
                        <span className={styles.h2hLabel}>All-Time Record</span>
                        {isFirstMatchup ? (
                            <p className={styles.h2hFirstMatchup}>First time playing each other!</p>
                        ) : (
                            <div className={styles.h2hRow}>
                                <div className={styles.h2hSide}>
                                    <span className={styles.h2hTeamHome}>You & {teammate?.username}</span>
                                    <span className={`${styles.h2hBigNum} ${myWins > myLosses ? styles.h2hWinning : ''}`}>{myWins}</span>
                                </div>
                                <span className={styles.h2hDash}>–</span>
                                <div className={styles.h2hSide}>
                                    <span className={styles.h2hTeamAway}>{opp1?.username} & {opp2?.username}</span>
                                    <span className={`${styles.h2hBigNum} ${myLosses > myWins ? styles.h2hWinning : ''}`}>{myLosses}</span>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })()}

            {/* Buttons */}
            {error && <p className={styles.error}>{error}</p>}
            <div className={styles.buttonArea}>
                {allReady && isCreator && (
                    <button className={styles.startBtn} onClick={handleStart}>
                        Start Game
                    </button>
                )}
                {allReady && !isCreator && (
                    <p className={styles.readyMsg}>All players ready — waiting for host to start...</p>
                )}
                {isCreator && (
                    <button
                        className={`${styles.cancelBtn} ${confirmCancel ? styles.cancelBtnConfirm : ''}`}
                        onClick={handleCancelClick}
                    >
                        {confirmCancel ? 'Tap again to cancel' : 'Cancel Game'}
                    </button>
                )}
            </div>
        </div>
    );
};

export default Lobby;
